import { useEffect, useRef } from "react";
import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";
import { isDayjs } from "dayjs";

type TUseTriggerValidateProps<T extends FieldValues> = {
  methods: UseFormReturn<T, object, T>;
  name: Path<T>;
  triggerFields: Path<T>[];
};

type TTriggerValidatePair<T extends FieldValues> = {
  name: Path<T>;
  triggerFields: Path<T>[];
};

type TUseBatchTriggerValidateProps<T extends FieldValues> = {
  methods: UseFormReturn<T, object, T>;
  pairs: TTriggerValidatePair<T>[];
};

export const useTriggerValidate = <T extends FieldValues>({ methods, name, triggerFields }: TUseTriggerValidateProps<T>) => {
  const { control, trigger } = methods;
  const value = useWatch({ control, name });
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip validation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Skip trigger when value is an invalid Dayjs (e.g. user deleted YYYY/MM/DD partially)
    if (isDayjs(value) && !value.isValid()) return;
    triggerFields.forEach((field) => {
      trigger(field);
    });
  }, [value]);
};

export const useBatchTriggerValidate = <T extends FieldValues>({ methods, pairs }: TUseBatchTriggerValidateProps<T>) => {
  const { control, trigger } = methods;
  const names = pairs.map((pair) => pair.name);
  const values = useWatch({ control, name: names });
  const isFirstRender = useRef(true);
  const prevValues = useRef(values);

  useEffect(() => {
    // Skip validation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevValues.current = values;
      return;
    }

    values.forEach((value, index) => {
      if (Object.is(value, prevValues.current[index])) return;
      // Skip trigger when value is an invalid Dayjs (e.g. user deleted YYYY/MM/DD partially)
      if (isDayjs(value) && !value.isValid()) return;
      pairs[index]?.triggerFields.forEach((field) => {
        trigger(field);
      });
    });

    prevValues.current = values;
  }, [values]);
};
